/**
 * Tag Report component
 * Shows summary of tag generation status and allows bulk tag generation
 * Uses the same filters and table as the Predictions Table tab
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { CheckCircle, HourglassEmpty } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SortingState } from '@tanstack/react-table';
import { predictionsApi } from '../services/predictions.service';
import { tagsApi } from '../services/tags.service';
import { Product, ExperimentVersion, ReviewStatus, PredictionFilters } from '../types';
import PredictionsFilters from './PredictionsFilters';
import PredictionsTable from './PredictionsTable';

interface TagReportProps {
  product: Product;
  experiment: ExperimentVersion;
}

export default function TagReport({ product, experiment }: TagReportProps) {
  const queryClient = useQueryClient();
  const [generationStatus, setGenerationStatus] = useState<{
    inProgress: boolean;
    results?: { success: number; failed: number; errors: string[] };
  }>({ inProgress: false });

  // Independent filter state for Tag Report
  const [filters, setFilters] = useState<PredictionFilters>(() => {
    const saved = localStorage.getItem('tag-report-filters');
    return saved ? JSON.parse(saved) : {};
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageHistory, setPageHistory] = useState<Array<Record<string, any> | null>>([null]);
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem('tag-report-page-size');
    return saved ? Number(saved) : 25;
  });

  const pageSizeOptions = [25, 50, 100, 250, 500, 1000];

  // Persist filter state
  useEffect(() => {
    localStorage.setItem('tag-report-filters', JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    localStorage.setItem('tag-report-page-size', pageSize.toString());
  }, [pageSize]);

  // Reset pagination on filter/page size change
  useEffect(() => {
    setCurrentPage(1);
    setPageHistory([null]);
  }, [filters, pageSize, product, experiment]);

  // Fetch filter values
  const { data: filterValues } = useQuery({
    queryKey: ['filter-values', product, experiment],
    queryFn: () => predictionsApi.getFilterValues(product, experiment),
  });

  // Fetch S3 prediction IDs
  const { data: s3PredictionIds = [] } = useQuery({
    queryKey: ['s3-prediction-ids', product, experiment],
    queryFn: () => predictionsApi.getS3PredictionIds(product, experiment),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch predictions with filters and pagination
  const { data: predictionsData, isLoading, error } = useQuery({
    queryKey: [
      'predictions',
      product,
      experiment,
      'tag-report',
      filters,
      currentPage,
      pageSize,
      pageHistory[currentPage - 1],
    ],
    queryFn: () =>
      predictionsApi.list(product, experiment, {
        filters,
        page: currentPage,
        pageSize,
        lastEvaluatedKey: pageHistory[currentPage - 1] || undefined,
      }),
    placeholderData: (previousData) => previousData,
  });

  const predictions = predictionsData?.items || [];
  const pagination = useMemo(
    () => ({
      currentPage,
      hasNextPage: predictionsData?.pagination.hasNextPage || false,
      hasPreviousPage: currentPage > 1,
    }),
    [currentPage, predictionsData]
  );

  const handlePageChange = (direction: 'next' | 'prev') => {
    if (direction === 'next' && pagination.hasNextPage) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      if (predictionsData?.pagination.lastEvaluatedKey) {
        setPageHistory((prev) => {
          const newHistory = [...prev];
          newHistory[newPage - 1] = predictionsData.pagination.lastEvaluatedKey || null;
          return newHistory;
        });
      }
    } else if (direction === 'prev' && pagination.hasPreviousPage) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const availableFilters = useMemo(
    () => ({
      grades: filterValues?.grades || [],
      units: filterValues?.units || [],
      reviewers: filterValues?.reviewers || [],
      assignees: filterValues?.assignees || [],
    }),
    [filterValues]
  );

  // Calculate statistics from currently loaded predictions
  const approvedPredictions = predictions.filter((p) => p.review_status === ReviewStatus.APPROVED);
  const approvedWithoutTags = approvedPredictions.filter(
    (p) => !p.spine_tag_ids || p.spine_tag_ids.length === 0
  );

  const stats = {
    total: predictions.length,
    approved: approvedPredictions.length,
    withTags: predictions.filter((p) => p.spine_tag_ids && p.spine_tag_ids.length > 0).length,
    withoutTags: predictions.filter((p) => !p.spine_tag_ids || p.spine_tag_ids.length === 0).length,
    approvedWithoutTags: approvedWithoutTags.length,
  };

  const approvedPct = stats.total > 0 ? (stats.approved / stats.total) * 100 : 0;
  const withTagsPct = stats.total > 0 ? (stats.withTags / stats.total) * 100 : 0;
  const withoutTagsPct = stats.total > 0 ? (stats.withoutTags / stats.total) * 100 : 0;

  // Generate tags mutation (only for filtered APPROVED predictions)
  const generateTagsMutation = useMutation({
    mutationFn: async () => {
      if (approvedWithoutTags.length === 0) {
        throw new Error('No approved predictions without tags');
      }

      return tagsApi.generateBulk(
        product,
        experiment,
        approvedWithoutTags.map((p) => p.id)
      );
    },
    onSuccess: (results) => {
      const successCount = results.filter((r) => r.success).length;
      const failedCount = results.filter((r) => !r.success).length;
      const errors = results.filter((r) => !r.success).map((r) => r.error || 'Unknown error');

      setGenerationStatus({
        inProgress: false,
        results: { success: successCount, failed: failedCount, errors },
      });

      queryClient.invalidateQueries({ queryKey: ['predictions'] });
    },
    onError: (error: any) => {
      setGenerationStatus({
        inProgress: false,
        results: {
          success: 0,
          failed: 1,
          errors: [error.message || 'Failed to generate tags'],
        },
      });
    },
  });

  const handleGenerateTags = () => {
    setGenerationStatus({ inProgress: true });
    generateTagsMutation.mutate();
  };

  if (error) {
    return (
      <Alert severity="error">
        Failed to load tag report data: {(error as any)?.message || 'Unknown error'}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ mb: 2 }}>
        <PredictionsFilters
          filters={filters}
          onFiltersChange={setFilters}
          availableFilters={availableFilters}
        />
      </Box>

      <Typography variant="h6" gutterBottom>
        Tag Coverage Statistics
      </Typography>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h4" color="primary">
              {stats.total}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Total Predictions
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h4" color="primary">
              {stats.approved}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Approved ({approvedPct.toFixed(1)}%)
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle color="success" />
              <Typography variant="h4" color="success.main">
                {stats.withTags}
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary">
              With Tags ({withTagsPct.toFixed(1)}%)
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HourglassEmpty color="warning" />
              <Typography variant="h4" color="warning.main">
                {stats.withoutTags}
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary">
              Without Tags ({withoutTagsPct.toFixed(1)}%)
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Generate Tags Button */}
      <Box sx={{ mb: 3 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Only <strong>APPROVED</strong> predictions will have tags generated to ensure quality control.
        </Alert>

        {stats.approvedWithoutTags === 0 ? (
          stats.approved === 0 ? (
            <Alert severity="warning">
              No approved predictions found. Please approve predictions before generating tags.
            </Alert>
          ) : (
            <Alert severity="success">
              All approved predictions have tags generated!
            </Alert>
          )
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              {stats.approvedWithoutTags} approved predictions are missing tags.
            </Alert>
            <Button
              variant="contained"
              onClick={handleGenerateTags}
              disabled={generationStatus.inProgress}
              startIcon={generationStatus.inProgress ? <CircularProgress size={20} /> : null}
            >
              {generationStatus.inProgress
                ? 'Generating Tags...'
                : `Generate Tags for ${stats.approvedWithoutTags} Approved Predictions`}
            </Button>
          </>
        )}
      </Box>

      {/* Generation Results */}
      {generationStatus.results && (
        <Box sx={{ mb: 3 }}>
          {generationStatus.results.success > 0 && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Successfully generated tags for {generationStatus.results.success} predictions
            </Alert>
          )}
          {generationStatus.results.failed > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                Failed to generate tags for {generationStatus.results.failed} predictions
              </Typography>
              {generationStatus.results.errors.length > 0 && (
                <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                  {generationStatus.results.errors.slice(0, 5).map((error, idx) => (
                    <li key={idx}>
                      <Typography variant="body2">{error}</Typography>
                    </li>
                  ))}
                  {generationStatus.results.errors.length > 5 && (
                    <li>
                      <Typography variant="body2">
                        ... and {generationStatus.results.errors.length - 5} more errors
                      </Typography>
                    </li>
                  )}
                </Box>
              )}
            </Alert>
          )}
        </Box>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Table */}
      {!isLoading && (
        <>
          <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {pagination.hasNextPage === false && pagination.hasPreviousPage === false && Object.keys(filters).some(key => {
                const filterValue = filters[key as keyof PredictionFilters];
                return filterValue && (Array.isArray(filterValue) ? filterValue.length > 0 : filterValue);
              })
                ? `Showing ${predictions.length} filtered results`
                : `Showing ${predictions.length} predictions (Page ${currentPage})`}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Records per page</InputLabel>
                <Select
                  value={pageSize}
                  label="Records per page"
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {pageSizeOptions.map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          <PredictionsTable
            predictions={predictions}
            sorting={sorting}
            onSortingChange={setSorting}
            pagination={pagination}
            onPageChange={handlePageChange}
            product={product}
            experiment={experiment}
            availableAssignees={availableFilters.assignees}
            s3PredictionIds={s3PredictionIds}
          />
        </>
      )}
    </Box>
  );
}
